import styled from "@emotion/styled";
import { isEqual } from "lodash";
import { FC, useState } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, NumericInput, Picker, SpacingToken, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { IRuleTriggerSecondsPassedSinceEvent } from "store/entities/rules/interfaces";
import { plural } from "utils/string";

import { guessTimeUnit, timeUnitLabel } from "../../editor/input";
import { TIME_CONDITION_EVENTS, TimeConditionEventType } from "../../editor/utils";
import { changeTriggerValues } from "../utils";
import { withVar } from "themes/helpers";

interface IProps {
  input: FieldInputProps<IRuleTriggerSecondsPassedSinceEvent>;
}

const EVENT_PICKER_OPTION_KEYS = [
  "any",
  "status",
  "message",
  "assignment",
  "followUpSent",
  "priority",
  "ticketCreation",
];

type EventPickerOptionKey = (typeof EVENT_PICKER_OPTION_KEYS)[number];

const EVENT_NAME: Record<EventPickerOptionKey, string> = {
  any: "any ticket activity",
  status: "status changed",
  message: "message sent",
  assignment: "assignment changed",
  followUpSent: "follow-up sent",
  priority: "priority changed",
  ticketCreation: "ticket created",
};

const ANY_OPTION_KEY = "any";

export const TimePassedSinceTrigger: FC<IProps> = ({ input }) => {
  const { eventTypeIn, eq } = input.value;

  const [timeUnit, setTimeUnit] = useState(guessTimeUnit(eq));
  const [numberInputValue, setNumberInputValue] = useState(eq / guessTimeUnit(eq));
  const timeLabel = timeUnitLabel(timeUnit);

  const eventOptions: IPickerListItem[] = EVENT_PICKER_OPTION_KEYS.map(createEventOption);

  const selectedEventOptions: IPickerListItem[] = isEqual(TIME_CONDITION_EVENTS, eventTypeIn)
    ? [createEventOption(ANY_OPTION_KEY)]
    : eventTypeIn.map(createEventOption);

  const handleSelectEvent = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { eq, eventTypeIn: [] });
      return;
    }

    /**
     * We need to determine which option is currently selected.
     * We have to handle cases:
     * - We are selecting "any" and other options are selected - we need to
     * remove other options
     * - We are selecting other options and "any" is selected - we need to
     * remove "any"
     * - We are selecting other options and "any" is not selected - we can just
     * update the filter
     */
    const currentlySelectedOption: IPickerListItem =
      selectedOptions.length > selectedEventOptions.length
        ? selectedOptions.filter(option => !selectedEventOptions.includes(option))[0]
        : selectedEventOptions.filter(option => !selectedOptions.includes(option))[0];

    const isAnyCurrentlySelected = currentlySelectedOption.key === ANY_OPTION_KEY;

    const isAnyPreviouslySelected = selectedEventOptions.some(option => option.key === ANY_OPTION_KEY);

    if (isAnyCurrentlySelected) {
      // If we are selecting "any" we need to remove all other options
      changeTriggerValues(input, { eq, eventTypeIn: TIME_CONDITION_EVENTS as unknown as TimeConditionEventType[] });
    } else if (isAnyPreviouslySelected) {
      // If we are selecting other options and "any" was previously selected, we need to remove "any"
      changeTriggerValues(input, {
        eq,
        eventTypeIn: selectedOptions
          .filter(option => option.key !== ANY_OPTION_KEY)
          .map(option => option.key as TimeConditionEventType),
      });
    } else {
      // If we are selecting other options and "any" was not previously selected, we can just update the filter
      changeTriggerValues(input, {
        eq,
        eventTypeIn: selectedOptions.map(option => option.key as TimeConditionEventType),
      });
    }
  };

  const handleSecondsChange = (value: string): void => {
    const parsedValue = parseInt(value);
    if (isNaN(parsedValue)) {
      changeTriggerValues(input, { eq: timeUnit });
      setNumberInputValue(1);
      return;
    }

    setNumberInputValue(parsedValue);
    changeTriggerValues(input, { eq: parsedValue * timeUnit });
  };

  const timeConditionUnitOptions: IPickerListItem[] = [
    createTimeConditionUnitOption("seconds", numberInputValue),
    createTimeConditionUnitOption("minutes", numberInputValue),
    createTimeConditionUnitOption("hours", numberInputValue),
    createTimeConditionUnitOption("days", numberInputValue),
  ];

  const selectedTimeConditionUnitOption = [createTimeConditionUnitOption(timeLabel, numberInputValue)];

  const handleTimeConditionUnitChange = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const timeInSecondsForSelectedUnit = timeLabelToSeconds(selectedOptions[0].key);

    setTimeUnit(timeInSecondsForSelectedUnit);
    changeTriggerValues(input, { eq: numberInputValue * timeInSecondsForSelectedUnit });
  };

  return (
    <>
      <Picker
        type="multi"
        options={eventOptions}
        selected={selectedEventOptions}
        onSelect={handleSelectEvent}
        placeholder="Select one event or more…"
        searchDisabled
        data-testid="time-condition-event-type"
      />
      <Flex alignItems="center">
        <Label
          css={{
            whiteSpace: "nowrap",
            minWidth: "fit-content", 
            flexShrink: 0, 
            marginRight: withVar(SpacingToken.Spacing2),
          }}
        >
          is at least
        </Label>
        <NumberInput
          type="number"
          pattern="\d+"
          step="1"
          min={0}
          value={numberInputValue.toString()}
          // TODO: remove this once we have a proper type for the onChange handler
          // @ts-expect-error TS(2339)
          onChange={handleSecondsChange}
          data-testid="time-condition-value"
        />
        <Picker
          options={timeConditionUnitOptions}
          selected={selectedTimeConditionUnitOption}
          onSelect={handleTimeConditionUnitChange}
          hideClearButton
          searchDisabled
          data-testid="time-condition-unit"
        />
      </Flex>
    </>
  );
};

const createEventOption = (eventType: string): IPickerListItem => ({
  key: eventType,
  name: EVENT_NAME[eventType] ?? eventType,
});

const createTimeConditionUnitOption = (timeLabel: string, timeValue: number): IPickerListItem => {
  switch (timeLabel) {
    case "minutes":
      return { key: "minutes", name: plural(timeValue, "minute", "minutes") };
    case "hours":
      return { key: "hours", name: plural(timeValue, "hour", "hours") };
    case "days":
      return { key: "days", name: plural(timeValue, "day", "days") };
    case "seconds":
    default:
      return { key: "seconds", name: plural(timeValue, "second", "seconds") };
  }
};

const timeLabelToSeconds = (timeLabel: string): number => {
  switch (timeLabel) {
    case "minutes":
      return 60;
    case "hours":
      return 3600;
    case "days":
      return 86400;
    case "seconds":
    default:
      return 1;
  }
};

const Label = styled(Text)`
  min-width: 40px;
`;

const NumberInput = styled(NumericInput)`
  min-width: 60px;
  margin-right: var(${SpacingToken.Spacing2});
`;
